import { localApi } from '../../services/localApi'

export interface RequestDraft {
  title: string
  type: 'bug' | 'improvement' | 'feature' | 'integration' | 'operation' | null
  team: 'commercial' | 'operations' | null
  area: 'labeling' | 'technical_sheets' | 'quality' | 'traceability' | 'sac' | 'account' | 'website' | 'other' | null
  priority: 'P0' | 'P1' | 'P2' | 'P3'
  description: string
  expectedResult: string
  impact: string
  origin: 'internal' | 'customer' | 'prospect'
  customer: string
  desiredDate: string | null
  dateReason: string
  steps: string
  actualResult: string
  frequency: string
  affectedRecords: string
  evidenceLinks: string[]
}

export interface RequestSubmission extends RequestDraft { id: string, attachmentIds: string[] }
export interface RequestAttachment { id: string, name: string, size: number }
export interface RequestStatusEvent { version: number, status: string | null, recordedAt: string | null }
export interface RequestReceipt extends RequestDraft {
  id: string
  requesterName: string
  requesterEmail: string
  state: 'pending' | 'linked' | 'needs_review'
  status: string | null
  issueUrl: string | null
  issueNumber: number | null
  createdAt: string
  attachments: RequestAttachment[]
  history: RequestStatusEvent[]
}
export interface RequestAccess {
  allowed: boolean
  name: string
  email: string
  aiAvailable: boolean
  submissionAvailable: boolean
}
export interface RequestChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  state?: 'streaming' | 'failed'
}
export interface DraftChange { field: Exclude<keyof RequestDraft, 'evidenceLinks'>, value: string }
interface Envelope<T> { data: T }
const PATH = '/v1/internal-requests'

export const internalRequestsApi = {
  async access (): Promise<RequestAccess> {
    return (await localApi.get<Envelope<RequestAccess>>(`${PATH}/access`)).data.data
  },
  async submit (data: RequestSubmission | string): Promise<RequestReceipt> {
    return (await localApi.post<Envelope<RequestReceipt>>(PATH, data)).data.data
  },
  async get (id: string): Promise<RequestReceipt> {
    return (await localApi.get<Envelope<RequestReceipt>>(`${PATH}/${encodeURIComponent(id)}`)).data.data
  },
  async chat (draft: RequestDraft, messages: RequestChatMessage[], signal: AbortSignal): Promise<{ message: string, changes: DraftChange[] }> {
    const response = await localApi.post<Envelope<{ message: string, changes: DraftChange[] }>>(
      `${PATH}/chat`, { draft, messages: messages.slice(-20).map(({ role, content }) => ({ role, content })) }, { signal }
    )
    return response.data.data
  },
  async transcribe (blob: Blob, signal: AbortSignal): Promise<string> {
    const data = new FormData()
    data.append('file', blob, blob.type.includes('mp4') ? 'solicitacao.mp4' : 'solicitacao.webm')
    const response = await localApi.post<Envelope<{ text: string }>>(`${PATH}/transcription`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }, signal
    })
    return response.data.data.text
  },
  async upload (file: File): Promise<RequestAttachment> {
    const data = new FormData()
    data.append('file', file)
    return (await localApi.post<Envelope<RequestAttachment>>(`${PATH}/attachments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })).data.data
  },
  async download (file: RequestAttachment): Promise<void> {
    const response = await localApi.get<Blob>(`${PATH}/attachments/${encodeURIComponent(file.id)}`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
  }
}
